import React, { useEffect, useState } from "react";
import { AboutImg, ProductImage } from "../../../../images/Images";
import RatingSvg from "../../components/svgs/RatingSvg";
import LocationSvg from "../../components/svgs/LocationSvg";
import ClockSvg from "../../components/svgs/ClockSvg";
import "./SingleVendor.css";
import GradientButton from "../../../../components/button/Button";
import CartSvg from "../products/svgs/CartSvg";
import axiosInstance from "../../../../axiosInstance";
import { useParams } from "react-router";
import LoadingComponent from "../../../../components/loading/Loading";
import { useCart } from "../../../../context/CartContext";
import CartSummary from "../cart/component/CartSummary";
import RetailCart from "../cart/Carts";
import { QuantitySelector } from "../products/ProductDetails";
import { useNavigate } from "react-router-dom";

interface Owner {
  id: string;
  full_name: string;
  email: string;
  phone_number: string;
  is_verified_email: boolean;
  membership_type: string;
  role: string;
  avatar: string;
}

interface Address {
  id: string;
  street: string;
  city: string;
  state: string;
  country: string;
  address_type: string;
}

interface Product {
  id: string;
  name: string;
  stock_count: number;
  amount: number;
  unit: string;
  description: string;
  image: string;
  tags: string;
}

interface Venture {
  id: string;
  name: string;
  tagline: string;
  description: string;
  email: string;
  phone: string;
  tax_id: string;
  logo: string | null;
  is_verified: boolean;
  stripe_account_id: string | null;
  payment_status: string;
  can_receive_payments: boolean;
  owner: Owner;
  addresses: Address[];
  products: Product[];
}

interface Category {
  id: number;
  name: string;
}

const SingleVendor: React.FC = () => {
  const { addToCart, removeFromCart, cart, updateQuantity } = useCart();
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [categories, setCategories] = useState<Category[]>([]);
  const [venture, setVenture] = useState<Venture | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const getDetails = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/ventures/${id}`);
      if (res.data && res.data.data) {
        setVenture(res.data.data);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDetails();
  }, [id]);

  const getCategories = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/segments");
      if (res.data) {
        setCategories(res.data.data);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCategories();
  }, []);

  const isInCart = (itemId: string) =>
    cart.some((cartItem) => cartItem.id === itemId);

  if (loading || !venture) {
    return <LoadingComponent />;
  }

  // Filter products by category if needed
  const filteredProducts =
    activeCategory === "all"
      ? venture.products
      : venture.products.filter(
          (product) =>
            product.tags &&
            product.tags.toLowerCase().includes(activeCategory.toLowerCase())
        );

  // Cart summary calculations
  const subTotal = cart.reduce(
    (sum, item) => sum + item.amount * item.quantity,
    0
  );
  const tax = subTotal * 0.05;
  const total = subTotal + tax;

  return (
    <div className="single-vendor-container">
      <div className="single-vendor-page">
        {/* Banner */}
        <div className="single-vendor-banner">
          <img
            src={
              venture.logo ||
              "https://img.freepik.com/free-photo/two-women-viewing-content-phone-local-african-market_181624-35124.jpg?uid=R27806234&ga=GA1.1.1994711177.1748430137&semt=ais_items_boosted&w=740"
            }
            alt="Store Banner"
            className="single-vendor-banner-img"
          />
          <div className="single-vendor-banner-overlay" />
          <div className="single-vendor-banner-content">
            <h1 className="single-vendor-name">{venture.name}</h1>
            <p className="single-vendor-tagline">
              {venture?.addresses[0]?.city}, {venture?.addresses[0]?.state}
            </p>
          </div>
        </div>

        <div className="single-vendor-main-layout">
          <div className="single-vendor-main-left">
            {/* Store Info & Products */}
            <div className="single-vendor-main">
              {/* <div className="single-vendor-desc">
                <LocationSvg />
                <span>{venture.description}</span>

              <span style={{ marginLeft: 12 }}>
                <ClockSvg /> 2 - 3 days
              </span>
              <span style={{ marginLeft: 12 }}>
                <RatingSvg /> 5.0 (100)
              </span> 
              </div> */}

              {/* Category Filter */}
              <div className="single-vendor-categories">
                <button
                  className={`vendor-cat-btn${
                    activeCategory === "all" ? " active" : ""
                  }`}
                  onClick={() => setActiveCategory("all")}
                >
                  All
                </button>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    className={`vendor-cat-btn${
                      activeCategory === category.name ? " active" : ""
                    }`}
                    onClick={() => setActiveCategory(category.name)}
                  >
                    {category.name}
                  </button>
                ))}
              </div>

              {/* Products Grid */}
              <div className="single-vendor-products-grid">
                {filteredProducts.length === 0 ? (
                  <div className="vendor-no-items">
                    No item found in this category.
                  </div>
                ) : (
                  filteredProducts.map((item) => (
                    <div className="vendor-product-card" key={item.id}>
                      <img
                        src={item.image || ProductImage}
                        alt={item.name}
                        className="vendor-product-img"
                      />
                      <div className="vendor-product-info">
                        <h3 className="vendor-product-name">{item.name}</h3>
                        <p className="vendor-product-stock">
                          {item.stock_count} Units Left
                        </p>
                        <div className="vendor-product-price">
                          ${item.amount?.toFixed(2)} {item.unit}
                        </div>
                      </div>
                      <div className="vendor-product-actions">
                        {isInCart(item.id) ? (
                          <QuantitySelector
                            value={
                              cart.find((ci) => ci.id === item.id)?.quantity ||
                              1
                            }
                            min={1}
                            max={item.stock_count}
                            onChange={(qty) => updateQuantity(item.id, qty)}
                            onDelete={() => removeFromCart(item.id)}
                          />
                        ) : (
                          <GradientButton
                            text="Add to cart"
                            svg={<CartSvg />}
                            onClick={() => {
                              const cartItem = {
                                id: item.id,
                                name: item.name,
                                amount: item.amount,
                                unit: item.unit,
                                image: item.image,
                                quantity: 1,
                                stock_count: item.stock_count,
                                storeName: venture.name,
                              };
                              addToCart(cartItem);
                            }}
                          />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          {/* <div className="single-vendor-main-right"></div> */}
        </div>
      </div>

      {/* Desktop/Tablet Cart & Summary */}
      <div className="single-vendor-cart-desktop">
        <RetailCart />
        <div className="single-vendor-cart-desktop-mobile">
          <CartSummary subTotal={subTotal} tax={tax} total={total} />
        </div>
      </div>

      {/* Floating Cart Button (mobile only) */}
      <div
        className="floating-cart-btn"
        onClick={() => (window.location.href = "/retail-carts")}
        style={{ display: "none" }} // default hidden, CSS will show on mobile
      >
        <span className="floating-cart-btn__icon">
          <CartSvg color="#fff" />
        </span>
        {cart.length > 0 && (
          <span className="floating-cart-btn__count">{cart.length}</span>
        )}
      </div>
    </div>
  );
};

export default SingleVendor;
